import { Box, Stack, Typography } from "@mui/material";
import { BarChart } from "@mui/x-charts/BarChart";
import type { ReactNode } from "react";

import { ds } from "../../theme/ds";
import { chartPalette } from "../../theme/tokens";
import type { WidgetProps } from "../types";

import { Card, CardBody, CardFigure, CardLabel, CardTitle } from "./card";
import { makeItemTooltip, tooltipPopperProps } from "./ChartTooltip";
import { formatCurrency0, formatGeneratedAt, formatInt } from "./format";

// Where the supplier's listings sit against the category we observed, on the
// design system: a `.card` for the supplier's own listing, two charts on the
// chart spec (one series each, CHART_COLORS[1] Blue, dashed gray-100 gridlines
// on the value axis, no legend) and the `.bc-table` behind them (navy header,
// uppercase 11px, gray-200 row rules, hover gray-50). No methodology note. The supplier's own band is marked with a label, never a
// second colour.
export function CompetitorBenchmark({ widget }: WidgetProps<"competitor_benchmark">) {
    const { title, description } = widget.props;
    // `sampleNote` (the methodology note) is still accepted by the schema but no
    // longer rendered: Bill removed the "How this set was built" callout from
    // every room on 2026-09-08.
    const { subject, competitors, priceBands, source, generatedAt } = widget.data;

    // Reviews rolled up by brand: per-listing rows would give the band scale
    // duplicate category values and a band scale silently collapses them.
    const reviewRows = Object.entries(
        competitors.reduce<Record<string, number>>((acc, c) => {
            if (c.reviews == null) return acc;
            acc[c.brand] = (acc[c.brand] ?? 0) + c.reviews;
            return acc;
        }, {}),
    )
        .map(([brand, reviews]) => ({ brand, reviews }))
        .sort((a, b) => b.reviews - a.reviews)
        .slice(0, 8);

    const chartSx = {
        "& .MuiBarLabel-root": { fontSize: ds.text.xs, fill: ds.white, fontWeight: 600 },
        "& .MuiChartsAxis-line, & .MuiChartsAxis-tick": { stroke: ds.gray200 },
        "& .MuiChartsGrid-line": { stroke: ds.gray100, strokeDasharray: "4 4" },
    };
    const tick = (fill: string) => ({ fontSize: ds.text.xs, fill });
    const bandLabels = (priceBands ?? []).map((b) => (b.containsSubject ? `${b.label} (you)` : b.label));
    const BandTooltip = makeItemTooltip(bandLabels, (v) => `${formatInt(v)} listings`);
    const ReviewTooltip = makeItemTooltip(reviewRows.map((r) => r.brand), (v) => `${formatInt(v)} reviews`);

    return (
        <Box>
            <Typography variant="h4" sx={{ mb: description ? 0.5 : 1 }}>
                {title ?? "How the category is priced and reviewed"}
            </Typography>
            {description && (
                <Typography
                    component="div"
                    sx={{ fontSize: ds.text.sm, color: ds.gray600, mb: 1, maxWidth: 760 }}
                >
                    {description}
                </Typography>
            )}
            <Typography component="div" sx={{ fontSize: ds.text.xs, color: ds.gray500, mb: 2 }}>
                Snapshot · {formatGeneratedAt(generatedAt)} · {source}
            </Typography>

            {/* Where the supplier sits */}
            <Card sx={{ mb: "24px" }}>
                <CardTitle sx={{ mb: "12px" }}>{subject.name}</CardTitle>
                <Stack direction="row" spacing={4} sx={{ flexWrap: "wrap", rowGap: 1.5 }}>
                    <Figure label="Your price" value={formatCurrency0(subject.price)} />
                    <Figure label="Your reviews" value={formatInt(subject.reviews)} />
                    <Figure
                        label="Your rating"
                        value={subject.rating != null ? subject.rating.toFixed(1) : "-"}
                    />
                </Stack>
                {subject.note && <CardBody sx={{ mt: "8px" }}>{subject.note}</CardBody>}
            </Card>

            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                    gap: "16px",
                    mb: "24px",
                }}
            >
                {priceBands && priceBands.length > 0 && (
                    <ChartCard
                        heading="Price bands in the category"
                        caption="Listings observed per price band. Your band is marked."
                    >
                        <BarChart
                            height={260}
                            xAxis={[
                                {
                                    scaleType: "band" as const,
                                    data: priceBands.map((b) =>
                                        b.containsSubject ? `${b.label} (you)` : b.label,
                                    ),
                                    tickLabelStyle: tick(ds.gray700),
                                },
                            ]}
                            yAxis={[{ tickLabelStyle: tick(ds.gray500), tickNumber: 4 }]}
                            grid={{ horizontal: true }}
                            series={[
                                {
                                    data: priceBands.map((b) => b.count),
                                    label: "Listings",
                                    color: chartPalette[0],
                                },
                            ]}
                            barLabel="value"
                            margin={{ left: 36, right: 8, top: 16, bottom: 24 }}
                            tooltip={{ trigger: "item" }}
                            slots={{ itemContent: BandTooltip }}
                            slotProps={{ legend: { hidden: true }, popper: tooltipPopperProps }}
                            borderRadius={4}
                            sx={chartSx}
                        />
                    </ChartCard>
                )}

                {reviewRows.length > 0 && (
                    <ChartCard
                        heading="Reviews the category carries"
                        caption="Total reviews per brand across the listings observed."
                    >
                        <BarChart
                            height={260}
                            layout="horizontal"
                            yAxis={[
                                {
                                    scaleType: "band" as const,
                                    data: reviewRows.map((c) => c.brand),
                                    tickLabelStyle: tick(ds.gray700),
                                },
                            ]}
                            xAxis={[
                                {
                                    valueFormatter: (v: number) => formatInt(v),
                                    tickLabelStyle: tick(ds.gray500),
                                    tickNumber: 5,
                                },
                            ]}
                            grid={{ vertical: true }}
                            series={[
                                {
                                    data: reviewRows.map((c) => c.reviews),
                                    label: "Reviews",
                                    color: chartPalette[0],
                                    valueFormatter: (v: number | null) => formatInt(v),
                                },
                            ]}
                            barLabel={(item) => formatInt(item.value ?? null)}
                            margin={{ left: 96, right: 32, top: 16, bottom: 24 }}
                            tooltip={{ trigger: "item" }}
                            slots={{ itemContent: ReviewTooltip }}
                            slotProps={{ legend: { hidden: true }, popper: tooltipPopperProps }}
                            borderRadius={4}
                            sx={chartSx}
                        />
                    </ChartCard>
                )}
            </Box>

            {/* The set behind the charts: the design system's .bc-table */}
            <Box
                sx={{
                    overflowX: "auto",
                    bgcolor: ds.white,
                    border: `1px solid ${ds.gray200}`,
                    borderRadius: `${ds.radius}px`,
                }}
            >
                <Box
                    component="table"
                    sx={{
                        width: "100%",
                        minWidth: 640,
                        borderCollapse: "separate",
                        borderSpacing: 0,
                        fontSize: ds.text.sm,
                    }}
                >
                    <Box component="thead">
                        <Box component="tr">
                            <Th align="left">Brand</Th>
                            <Th align="left">Listing</Th>
                            <Th align="right">Price</Th>
                            <Th align="right">Reviews</Th>
                            <Th align="right">Rating</Th>
                            <Th align="right">Est. monthly revenue</Th>
                        </Box>
                    </Box>
                    <Box component="tbody">
                        {competitors.map((c, i) => (
                            <Box
                                component="tr"
                                key={`${c.asin ?? c.title}-${i}`}
                                sx={{
                                    "& td": i > 0 ? { borderTop: `1px solid ${ds.gray200}` } : {},
                                    "&:hover td": { bgcolor: ds.gray50 },
                                }}
                            >
                                <Td align="left" bold>
                                    {c.brand}
                                </Td>
                                <Td align="left">{c.title}</Td>
                                <Td align="right">{formatCurrency0(c.price)}</Td>
                                <Td align="right">{formatInt(c.reviews)}</Td>
                                <Td align="right">
                                    {c.rating != null ? c.rating.toFixed(1) : "-"}
                                </Td>
                                <Td align="right">{formatCurrency0(c.monthlyRevenue ?? null)}</Td>
                            </Box>
                        ))}
                        {/* The supplier's own listing, in the same table, so the price and the review count sit beside the peers. */}
                        <Box
                            component="tr"
                            sx={{ "& td": { borderTop: `1px solid ${ds.gray200}`, bgcolor: ds.gray100 } }}
                        >
                            <Td align="left" bold>
                                {subject.name.split(" ")[0]} (you)
                            </Td>
                            <Td align="left" bold>
                                {subject.name}
                            </Td>
                            <Td align="right" bold>
                                {formatCurrency0(subject.price)}
                            </Td>
                            <Td align="right" bold>
                                {formatInt(subject.reviews)}
                            </Td>
                            <Td align="right" bold>
                                {subject.rating != null ? subject.rating.toFixed(1) : "-"}
                            </Td>
                            <Td align="right" bold>
                                -
                            </Td>
                        </Box>
                    </Box>
                </Box>
            </Box>

        </Box>
    );
}

function Figure({ label, value }: { label: string; value: string }) {
    return (
        <Box>
            <CardLabel>{label}</CardLabel>
            <CardFigure size={ds.text.xl}>{value}</CardFigure>
        </Box>
    );
}

function ChartCard({
    heading,
    caption,
    children,
}: {
    heading: string;
    caption?: string;
    children: ReactNode;
}) {
    return (
        <Card>
            <Typography
                component="div"
                sx={{ fontSize: 16, fontWeight: 700, color: ds.navy, lineHeight: 1.3 }}
            >
                {heading}
            </Typography>
            {caption && (
                <Typography
                    component="div"
                    sx={{ fontSize: ds.text.xs, color: ds.gray500, mt: "4px", mb: 1 }}
                >
                    {caption}
                </Typography>
            )}
            {children}
        </Card>
    );
}

// .bc-table thead th
function Th({ children, align }: { children: ReactNode; align: "left" | "right" }) {
    return (
        <Box
            component="th"
            sx={{
                textAlign: align,
                p: "12px 16px",
                bgcolor: ds.navy,
                color: ds.white,
                fontSize: ds.text.xs,
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                fontWeight: 600,
                whiteSpace: "nowrap",
            }}
        >
            {children}
        </Box>
    );
}

// .bc-table tbody td (+ .bold)
function Td({
    children,
    align,
    bold,
}: {
    children: ReactNode;
    align: "left" | "right";
    bold?: boolean;
}) {
    return (
        <Box
            component="td"
            sx={{
                textAlign: align,
                p: "10px 16px",
                color: bold ? ds.navy : ds.gray700,
                fontWeight: bold ? 700 : 400,
                whiteSpace: align === "right" ? "nowrap" : "normal",
            }}
        >
            {children}
        </Box>
    );
}
